/* Copyright (c) Trimble Inc. All rights reserved. */

import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { AuthContext, type AuthContextState } from '../../config/AuthConsumerContext.config';
import { type Environment, type AuthConfig } from '../../models/AuthConfig.model';
import { AuthConfigStore, LoginConfig } from '../../stores/AuthConfig.store';
import { SessionExpiryDialog } from '../session-expiry-dialog/index.component';
import { ErrorBoundary } from '@tcp-base/design-system';
import { SESSION_EXPIRY_MONITOR_ENABLED } from '../../config/CONSTANTS';

/**
 * AuthProvider Props
 */
export interface AuthProviderProps {
  children: ReactNode;
  clientId?: string;
  environment?: Environment;
  scopes?: readonly string[];
  onConfigured?: (authConfig: AuthConfig) => void;
  onError?: (error: Error) => void;
}

/**
 * Authentication Provider Component
 *
 * Primary connector and server for all auth-related workflows.
 * Receives clientId, scopes, and environment from the app and stores these details for reusing.
 *
 * Features:
 * - Receives and validates authentication configuration
 * - Provides authentication context to all child components
 * - Manages authentication state globally
 * - Supports both prop-based and manual configuration
 * - Stores configuration for reuse across the application
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  clientId,
  environment,
  scopes,
  onConfigured,
  onError,
}) => {
  const [state, setState] = useState<{
    clientId?: string;
    environment?: Environment;
    scopes?: readonly string[];
    authConfig?: AuthConfig;
    isConfigured: boolean;
  }>({
    isConfigured: false,
  });

  /**
   * Configure authentication with provided parameters
   */
  const configure = useCallback(
    (config: { clientId: string; environment: Environment; scopes: readonly string[] }) => {
      try {
        // Configure the auth store
        const authConfig = AuthConfigStore.configure(config);

        // Update local state
        setState({
          clientId: config.clientId,
          environment: config.environment,
          scopes: config.scopes,
          authConfig,
          isConfigured: true,
        });

        // Call success callback
        onConfigured?.(authConfig);
      } catch (error) {
        const authError = error instanceof Error ? error : new Error(String(error));

        // Clear state on error
        setState({ isConfigured: false });
        AuthConfigStore.clear();

        // Call error callback
        onError?.(authError);
      }
    },
    [onConfigured, onError],
  );

  /**
   * Get current auth configuration
   */
  const getAuthConfig = useCallback((): AuthConfig | null => {
    return AuthConfigStore.getAuthConfig();
  }, []);

  /**
   * Clear authentication configuration
   */
  const clear = useCallback(() => {
    AuthConfigStore.clear();
    setState({ isConfigured: false });
  }, []);

  /**
   * Auto-configure if props are provided
   */
  useEffect(() => {
    if (clientId && environment && scopes && scopes.length > 0) {
      configure({ clientId, environment, scopes });
    }
  }, [clientId, environment, scopes, configure]);

  /**
   * Context value
   */
  const contextValue: AuthContextState = {
    clientId: state.clientId,
    environment: state.environment,
    scopes: state.scopes,
    authConfig: state.authConfig,
    isConfigured: state.isConfigured,
    configure,
    getAuthConfig,
    clear,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {state.isConfigured && SESSION_EXPIRY_MONITOR_ENABLED && (
        <ErrorBoundary>
          <SessionExpiryDialog />
        </ErrorBoundary>
      )}
    </AuthContext.Provider>
  );
};

/**
 * Utility functions for accessing auth configuration outside of React context
 */

/**
 * Get authentication configuration (non-React contexts)
 */
export const getAuthConfiguration = (): AuthConfig | null => {
  return AuthConfigStore.getAuthConfig();
};

/**
 * Get login configuration for URL construction
 */
export const getLoginConfiguration = (): LoginConfig | null => {
  return AuthConfigStore.getLoginConfig();
};

/**
 * Check if authentication is configured (non-React contexts)
 */
export const isAuthConfigured = (): boolean => {
  return AuthConfigStore.isConfigured();
};

export default AuthProvider;
